import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User, Mail, Shield, Building2, Lock, Eye, EyeOff, CheckCircle,
  Bell, Palette, ChevronRight, ChevronDown, KeyRound, AlertTriangle, Trash2, Phone, Pencil, Download, ShieldCheck, History, Info,
} from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { auth as authApi, alertSubscriptions as alertSubscriptionsApi, type ApiError, type NexoraRole, type ResidentConsent, type AccessLogEntry } from '../../services/api';

const roleLabel: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin', SOCIETY_ADMIN: 'Society Admin',
  STAFF: 'Staff', VIEWER: 'Viewer', RESIDENT: 'Resident',
};
const roleColor: Record<string, string> = {
  SUPER_ADMIN: '#7C3AED', SOCIETY_ADMIN: '#1A6CF6',
  STAFF: '#10B981', VIEWER: '#64748B', RESIDENT: '#F59E0B',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--nx-border)', background: 'var(--nx-surface-2)',
  color: 'var(--nx-text-primary)', fontSize: 13, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'var(--font-body)',
};

/* Collapsible card */
const Section: React.FC<{
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, icon, open, onToggle, children }) => (
  <div style={{ background: 'var(--nx-surface-2)', border: '1px solid var(--nx-border)', borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: '16px 22px', background: 'none', border: 'none', cursor: 'pointer',
        borderBottom: open ? '1px solid var(--nx-border)' : 'none',
        transition: 'background 150ms',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--nx-surface-3)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(26,108,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nx-blue)', flexShrink: 0 }}>
        {icon}
      </span>
      <div style={{ flex: 1, textAlign: 'left' }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)' }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>{subtitle}</div>
      </div>
      {open
        ? <ChevronDown size={16} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
        : <ChevronRight size={16} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
      }
    </button>
    {open && <div style={{ padding: '20px 22px' }}>{children}</div>}
  </div>
);

const SettingsPage: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ fullName: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);
  const [changeModal, setChangeModal] = useState<'email' | 'phone' | null>(null);

  const startEditingProfile = () => {
    setProfileForm({ fullName: user?.fullName ?? '' });
    setProfileError('');
    setEditingProfile(true);
  };

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileForm.fullName.trim()) return;
    setProfileSaving(true);
    setProfileError('');
    try {
      const updated = await authApi.updateProfile({ fullName: profileForm.fullName.trim() });
      if (user) {
        updateUser({ ...user, fullName: updated.fullName });
      }
      setEditingProfile(false);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (err) {
      setProfileError((err as ApiError).message || 'Could not save your changes. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  const [open, setOpen] = useState({ profile: true, password: true, notifications: true, admin: true, alerts: false, consent: false, dataExport: false, accessLogs: false, whatWeCollect: false, danger: false });
  const toggle = (key: keyof typeof open) => setOpen(o => ({ ...o, [key]: !o[key] }));

  const isResident = user?.role === 'RESIDENT' || user?.role === 'TENANT';
  const PRIVACY_CONSENT_TYPE = 'PRIVACY_TERMS';

  const [consentStatus, setConsentStatus] = useState<ResidentConsent[]>([]);
  const [consentLoaded, setConsentLoaded] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState('');

  useEffect(() => {
    if (!isResident || !open.consent || consentLoaded) return;
    authApi.getMyConsent()
      .then(setConsentStatus)
      .catch(() => {})
      .finally(() => setConsentLoaded(true));
  }, [isResident, open.consent, consentLoaded]);

  const [accessLogs, setAccessLogs] = useState<AccessLogEntry[]>([]);
  const [accessLogsLoaded, setAccessLogsLoaded] = useState(false);
  const [accessLogsError, setAccessLogsError] = useState('');

  useEffect(() => {
    if (!open.accessLogs || accessLogsLoaded) return;
    authApi.getMyAccessLogs()
      .then(page => setAccessLogs(page.content))
      .catch(err => setAccessLogsError((err as ApiError).message || 'Could not load your access logs.'))
      .finally(() => setAccessLogsLoaded(true));
  }, [open.accessLogs, accessLogsLoaded]);

  const privacyConsent = consentStatus.find(c => c.consentType === PRIVACY_CONSENT_TYPE);
  const privacyConsentGranted = privacyConsent?.granted ?? false;

  const toggleMyConsent = async () => {
    setConsentBusy(true);
    setConsentError('');
    try {
      const updated = privacyConsentGranted
        ? await authApi.withdrawMyConsent(PRIVACY_CONSENT_TYPE)
        : await authApi.grantMyConsent(PRIVACY_CONSENT_TYPE);
      setConsentStatus(prev => [updated, ...prev.filter(c => c.consentType !== PRIVACY_CONSENT_TYPE)]);
    } catch (err) {
      setConsentError((err as ApiError).message || 'Could not update your consent. Please try again.');
    } finally {
      setConsentBusy(false);
    }
  };

  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState('');

  const downloadMyData = async () => {
    setExportBusy(true);
    setExportError('');
    try {
      const data = await authApi.exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'nexora-my-data.json';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError((err as ApiError).message || 'Could not export your data. Please try again.');
    } finally {
      setExportBusy(false);
    }
  };

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteStep, setDeleteStep] = useState<'confirm' | 'otp'>('confirm');
  const [otp, setOtp] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const openDeleteFlow = () => {
    setDeleteStep('confirm');
    setDeleteError('');
    setOtp('');
    setConfirmingDelete(true);
  };

  const requestOtp = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await authApi.requestAccountDeletionOtp();
      setDeleteStep('otp');
    } catch (err) {
      setDeleteError((err as ApiError).message || 'Could not send the code. Please try again.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmDeleteWithOtp = async () => {
    if (otp.trim().length !== 6) {
      setDeleteError('Enter the 6-digit code from your email.');
      return;
    }
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await authApi.deleteAccount(otp.trim());
      await logout();
      navigate('/');
    } catch (err) {
      setDeleteError((err as ApiError).message || 'Invalid or expired code. Please try again.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwForm, setPwForm]     = useState({ old: '', new: '', confirm: '' });
  const [pwSaved, setPwSaved]   = useState(false);

  const [notifications, setNotifications] = useState({
    visitors: true, deliveries: true, notices: true, events: false, billing: true,
  });

  const isAdmin = user?.role === 'SOCIETY_ADMIN' || user?.role === 'STAFF';

  const ALERT_ROLE_OPTIONS: { value: NexoraRole; label: string }[] = [
    { value: 'admin', label: 'Society Admin' },
    { value: 'staff', label: 'Staff' },
    { value: 'guard', label: 'Guard' },
    { value: 'treasurer', label: 'Treasurer' },
    { value: 'view-only-admin', label: 'View Only Admin' },
    { value: 'theme-manager', label: 'Theme Manager' },
    { value: 'analyst', label: 'Analyst' },
    { value: 'viewer', label: 'Viewer' },
    { value: 'resident', label: 'Resident' },
    { value: 'tenant', label: 'Tenant' },
  ];

  const [sosRoles, setSosRoles] = useState<Set<NexoraRole>>(new Set());
  const [childSafetyRoles, setChildSafetyRoles] = useState<Set<NexoraRole>>(new Set());
  const [alertsLoaded, setAlertsLoaded] = useState(false);
  const [savingAlerts, setSavingAlerts] = useState<'SOS' | 'CHILD_SAFETY' | null>(null);
  const [alertsSaved, setAlertsSaved] = useState<'SOS' | 'CHILD_SAFETY' | null>(null);

  useEffect(() => {
    if (!isAdmin || !user?.societyId || !open.alerts || alertsLoaded) return;
    const sid = user.societyId;
    Promise.all([alertSubscriptionsApi.get(sid, 'SOS'), alertSubscriptionsApi.get(sid, 'CHILD_SAFETY')])
      .then(([sos, child]) => {
        setSosRoles(new Set(sos));
        setChildSafetyRoles(new Set(child));
        setAlertsLoaded(true);
      })
      .catch(() => setAlertsLoaded(true));
  }, [isAdmin, user?.societyId, open.alerts, alertsLoaded]);

  const toggleAlertRole = (type: 'SOS' | 'CHILD_SAFETY', role: NexoraRole) => {
    const setter = type === 'SOS' ? setSosRoles : setChildSafetyRoles;
    setter(prev => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role); else next.add(role);
      return next;
    });
  };

  const saveAlertRoles = async (type: 'SOS' | 'CHILD_SAFETY') => {
    if (!user?.societyId) return;
    setSavingAlerts(type);
    try {
      const roles = Array.from(type === 'SOS' ? sosRoles : childSafetyRoles);
      await alertSubscriptionsApi.set(user.societyId, type, roles);
      setAlertsSaved(type);
      setTimeout(() => setAlertsSaved(null), 3000);
    } catch {
      // best-effort — the toggle state stays as the admin left it, they can retry Save
    } finally {
      setSavingAlerts(null);
    }
  };

  const handlePasswordSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwForm.old || !pwForm.new || pwForm.new !== pwForm.confirm) return;
    setPwSaved(true);
    setPwForm({ old: '', new: '', confirm: '' });
    setTimeout(() => setPwSaved(false), 3000);
  };

  const pwValid = pwForm.old && pwForm.new && pwForm.new === pwForm.confirm;

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Settings
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
          Manage your account, preferences, and notification settings.
        </p>
      </div>

      {/* Profile */}
      <Section id="profile" title="Profile" subtitle="Your account details and identity" icon={<User size={15} />} open={open.profile} onToggle={() => toggle('profile')}>
        {!editingProfile ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--nx-text-muted)' }}><User size={13} /> Full Name</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text-primary)' }}>{user?.fullName ?? '—'}</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--nx-text-muted)' }}><Mail size={13} /> Email Address</div>
                  <button onClick={() => setChangeModal('email')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--nx-blue)', fontWeight: 600, padding: 0 }}>Change</button>
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text-primary)' }}>{user?.email ?? '—'}</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--nx-text-muted)' }}><Shield size={13} /> Role</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text-primary)' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: `${roleColor[user?.role ?? 'RESIDENT']}18`, color: roleColor[user?.role ?? 'RESIDENT'] }}>
                    {roleLabel[user?.role ?? 'RESIDENT']}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--nx-text-muted)' }}><Building2 size={13} /> Community</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text-primary)' }}>{user?.societyName ?? 'Not assigned'}</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--nx-text-muted)' }}><Phone size={13} /> Mobile Number</div>
                  <button onClick={() => setChangeModal('phone')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--nx-blue)', fontWeight: 600, padding: 0 }}>Change</button>
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text-primary)' }}>{user?.phone || 'Not set'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--nx-border)' }}>
              <button onClick={startEditingProfile} className="nx-btn nx-btn-primary nx-btn-sm" style={{ gap: 6 }}>
                <Pencil size={13} /> Edit Name
              </button>
              {profileSaved && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#10B981' }}>
                  <CheckCircle size={13} /> Profile updated successfully
                </span>
              )}
            </div>
          </>
        ) : (
          <form onSubmit={saveProfile}>
            <div style={{ maxWidth: 340, marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Full Name
              </label>
              <input
                value={profileForm.fullName}
                onChange={e => setProfileForm({ fullName: e.target.value })}
                style={inputStyle}
                placeholder="Your full name"
              />
            </div>

            {profileError && (
              <p style={{ fontSize: 12, color: '#EF4444', marginBottom: 12 }}>{profileError}</p>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4, borderTop: '1px solid var(--nx-border)' }}>
              <button
                type="submit"
                disabled={profileSaving || !profileForm.fullName.trim()}
                className="nx-btn nx-btn-primary nx-btn-sm"
                style={{ gap: 6, opacity: profileSaving ? 0.6 : 1 }}
              >
                <CheckCircle size={13} /> {profileSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => setEditingProfile(false)}
                disabled={profileSaving}
                className="nx-btn nx-btn-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Section>

      <ChangeContactModal
        open={changeModal !== null}
        kind={changeModal ?? 'email'}
        currentValue={changeModal === 'phone' ? (user?.phone ?? '') : (user?.email ?? '')}
        onClose={() => setChangeModal(null)}
        onSaved={(updated) => {
          if (user) updateUser({ ...user, email: updated.email, phone: updated.phone });
        }}
      />

      {/* Change Password */}
      <Section id="password" title="Change Password" subtitle="Update your login password" icon={<KeyRound size={15} />} open={open.password} onToggle={() => toggle('password')}>
        <form onSubmit={handlePasswordSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
            {[
              { key: 'old', label: 'Current Password', show: showOld, toggle: () => setShowOld(v => !v) },
              { key: 'new', label: 'New Password', show: showNew, toggle: () => setShowNew(v => !v) },
              { key: 'confirm', label: 'Confirm New Password', show: showConfirm, toggle: () => setShowConfirm(v => !v) },
            ].map(field => (
              <div key={field.key}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {field.label}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={field.show ? 'text' : 'password'}
                    value={pwForm[field.key as keyof typeof pwForm]}
                    onChange={e => setPwForm(p => ({ ...p, [field.key]: e.target.value }))}
                    style={{ ...inputStyle, paddingRight: 36 }}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={field.toggle}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', display: 'flex' }}>
                    {field.show ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {pwForm.new && pwForm.confirm && pwForm.new !== pwForm.confirm && (
            <p style={{ fontSize: 11, color: '#EF4444', marginBottom: 12 }}>Passwords do not match.</p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4, borderTop: '1px solid var(--nx-border)' }}>
            <button
              type="submit"
              disabled={!pwValid}
              className="nx-btn nx-btn-primary nx-btn-sm"
              style={{ gap: 6, opacity: pwValid ? 1 : 0.45, cursor: pwValid ? 'pointer' : 'not-allowed' }}
            >
              <Lock size={13} /> Update Password
            </button>
            {pwSaved && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#10B981' }}>
                <CheckCircle size={13} /> Password updated successfully
              </span>
            )}
          </div>
        </form>
      </Section>

      {/* Notifications */}
      <Section id="notifications" title="Notifications" subtitle="Choose what you want to be notified about" icon={<Bell size={15} />} open={open.notifications} onToggle={() => toggle('notifications')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[
            { key: 'visitors',   label: 'Visitor approvals & arrivals', desc: 'Alerts when a visitor is registered or arrives' },
            { key: 'deliveries', label: 'Package deliveries',           desc: 'Notify when a delivery is logged for your unit' },
            { key: 'notices',    label: 'Noticeboard posts',            desc: 'New notices from society management' },
            { key: 'events',     label: 'Community events',             desc: 'Upcoming events and RSVP reminders' },
            ...(isAdmin ? [{ key: 'billing', label: 'Billing & dues alerts', desc: 'Payment reminders and invoice notifications' }] : []),
          ].map(item => (
            <div key={item.key}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderRadius: 8, transition: 'background 150ms' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--nx-surface-3)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Bell size={14} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text-primary)' }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{item.desc}</div>
                </div>
              </div>
              <button
                onClick={() => setNotifications(n => ({ ...n, [item.key]: !n[item.key as keyof typeof n] }))}
                style={{ width: 38, height: 21, borderRadius: 99, border: 'none', cursor: 'pointer', flexShrink: 0, position: 'relative', transition: 'background 200ms', background: notifications[item.key as keyof typeof notifications] ? 'var(--nx-primary-accent)' : 'var(--nx-border)' }}
              >
                <span style={{ position: 'absolute', top: 3, left: notifications[item.key as keyof typeof notifications] ? 19 : 3, width: 15, height: 15, borderRadius: '50%', background: '#fff', transition: 'left 200ms', display: 'block' }} />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* Admin quick links */}
      {isAdmin && (
        <Section id="admin" title="Community Administration" subtitle="Quick access to admin configuration" icon={<Shield size={15} />} open={open.admin} onToggle={() => toggle('admin')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { icon: <Palette size={14} />, label: 'Community Features', desc: 'Toggle noticeboard and events for residents', to: '/dashboard/community-features' },
              { icon: <Shield size={14} />, label: 'Security Logs', desc: 'Gate entry, patrol, and incident logs', to: '/dashboard/security' },
            ].map(link => (
              <Link key={link.to} to={link.to}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8, border: '1px solid var(--nx-border)', textDecoration: 'none', transition: 'background 150ms', background: 'var(--nx-surface-3)' }}
                onMouseEnter={e => ((e.currentTarget as HTMLElement).style.background = 'rgba(26,108,246,0.05)')}
                onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = 'var(--nx-surface-3)')}
              >
                <span style={{ color: 'var(--nx-primary-accent)' }}>{link.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text-primary)' }}>{link.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{link.desc}</div>
                </div>
                <ChevronRight size={14} style={{ color: 'var(--nx-text-muted)' }} />
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Alert recipients */}
      {isAdmin && (
        <Section id="alerts" title="Alert Recipients" subtitle="Choose which roles get notified for security alerts" icon={<Bell size={15} />} open={open.alerts} onToggle={() => toggle('alerts')}>
          {[
            { type: 'SOS' as const, title: 'Emergency SOS', roles: sosRoles, defaultHint: 'Default: Society Admin, Staff' },
            { type: 'CHILD_SAFETY' as const, title: 'Child Safety Alert', roles: childSafetyRoles, defaultHint: 'Default: everyone in the society' },
          ].map(block => (
            <div key={block.type} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-primary)', marginBottom: 2 }}>{block.title}</div>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 10 }}>
                {block.roles.size === 0 ? block.defaultHint : 'Custom recipients configured'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {ALERT_ROLE_OPTIONS.map(opt => {
                  const active = block.roles.has(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggleAlertRole(block.type, opt.value)}
                      style={{
                        padding: '6px 12px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                        border: active ? '1px solid var(--nx-primary-accent)' : '1px solid var(--nx-border)',
                        background: active ? 'rgba(26,108,246,0.12)' : 'var(--nx-surface-3)',
                        color: active ? 'var(--nx-primary-accent)' : 'var(--nx-text-secondary)',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => saveAlertRoles(block.type)}
                disabled={savingAlerts === block.type}
                className="nx-btn nx-btn-sm"
                style={{ marginTop: 12 }}
              >
                {savingAlerts === block.type ? 'Saving…' : alertsSaved === block.type ? 'Saved ✓' : 'Save'}
              </button>
            </div>
          ))}
        </Section>
      )}

      {/* Privacy & consent (residents only) */}
      {isResident && (
        <Section id="consent" title="Privacy & Consent" subtitle="Manage your data processing consent" icon={<ShieldCheck size={15} />} open={open.consent} onToggle={() => toggle('consent')}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', borderRadius: 8, background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)' }}>
            <ShieldCheck size={16} style={{ color: privacyConsentGranted ? '#10B981' : 'var(--nx-text-muted)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, color: 'var(--nx-text-primary)', margin: 0, marginBottom: 4, fontWeight: 500 }}>
                Privacy Policy & Terms of Service
              </p>
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: 0, marginBottom: 12 }}>
                {!consentLoaded
                  ? 'Loading your consent status…'
                  : privacyConsentGranted
                    ? `Consent granted${privacyConsent?.grantedAt ? ` on ${new Date(privacyConsent.grantedAt).toLocaleDateString()}` : ''} (version ${privacyConsent?.consentVersion}). Withdrawing may limit access to features that require processing your personal data.`
                    : 'You have withdrawn consent for data processing under Nexora\'s Privacy Policy and Terms of Service.'}
              </p>
              {consentError && <p style={{ fontSize: 12, color: '#EF4444', margin: '0 0 10px' }}>{consentError}</p>}
              <button
                onClick={toggleMyConsent}
                disabled={consentBusy || !consentLoaded}
                className="nx-btn nx-btn-sm"
                style={{
                  gap: 6,
                  opacity: consentBusy || !consentLoaded ? 0.7 : 1,
                  cursor: consentBusy || !consentLoaded ? 'not-allowed' : 'pointer',
                  ...(privacyConsentGranted ? { background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' } : {}),
                }}
              >
                <ShieldCheck size={13} /> {consentBusy ? 'Updating…' : privacyConsentGranted ? 'Withdraw Consent' : 'Grant Consent'}
              </button>
            </div>
          </div>
        </Section>
      )}

      {/* Data export */}
      <Section id="dataExport" title="Download My Data" subtitle="Get a copy of the personal data Nexora holds on your account" icon={<Download size={15} />} open={open.dataExport} onToggle={() => toggle('dataExport')}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', borderRadius: 8, background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)' }}>
          <Download size={16} style={{ color: 'var(--nx-blue)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: 0, marginBottom: 12 }}>
              Download a JSON file with your profile, contact, and account details as stored in Nexora.
            </p>
            {exportError && <p style={{ fontSize: 12, color: '#EF4444', margin: '0 0 10px' }}>{exportError}</p>}
            <button
              onClick={downloadMyData}
              disabled={exportBusy}
              className="nx-btn nx-btn-sm"
              style={{ gap: 6, opacity: exportBusy ? 0.7 : 1, cursor: exportBusy ? 'not-allowed' : 'pointer' }}
            >
              <Download size={13} /> {exportBusy ? 'Preparing…' : 'Download My Data'}
            </button>
          </div>
        </div>
      </Section>

      {/* What we collect */}
      <Section id="whatWeCollect" title="What We Collect" subtitle="The categories of personal data Nexora processes about you" icon={<Info size={15} />} open={open.whatWeCollect} onToggle={() => toggle('whatWeCollect')}>
        <ul style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: 0, paddingLeft: 18, lineHeight: 1.9 }}>
          <li><strong style={{ color: 'var(--nx-text-primary)' }}>Identity &amp; contact</strong> — full name, email, phone, username, unit/society assignment</li>
          <li><strong style={{ color: 'var(--nx-text-primary)' }}>Account activity</strong> — login timestamps, role, tenancy dates</li>
          {isResident && <>
            <li><strong style={{ color: 'var(--nx-text-primary)' }}>Helpdesk tickets</strong> you've raised</li>
            <li><strong style={{ color: 'var(--nx-text-primary)' }}>Community posts &amp; comments</strong> you've authored, including any attached photos</li>
            <li><strong style={{ color: 'var(--nx-text-primary)' }}>Marketplace listings</strong> you've posted</li>
            <li><strong style={{ color: 'var(--nx-text-primary)' }}>Visitor entries</strong> you've registered for your unit</li>
            <li><strong style={{ color: 'var(--nx-text-primary)' }}>Invoices &amp; billing</strong> tied to your unit</li>
          </>}
          <li><strong style={{ color: 'var(--nx-text-primary)' }}>Access &amp; audit records</strong> — an entry each time an action is taken on your account (see "My Access Logs" below)</li>
        </ul>
        <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', margin: '12px 0 0' }}>
          Use "Download My Data" above for the full export, or contact your society admin for anything not covered here.
        </p>
      </Section>

      {/* Access logs */}
      <Section id="accessLogs" title="My Access Logs" subtitle="See who and what has acted on your account" icon={<History size={15} />} open={open.accessLogs} onToggle={() => toggle('accessLogs')}>
        {accessLogsError && <p style={{ fontSize: 12, color: '#EF4444', margin: '0 0 10px' }}>{accessLogsError}</p>}
        {!accessLogsLoaded ? (
          <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: 0 }}>Loading your access logs…</p>
        ) : accessLogs.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: 0 }}>No recorded activity yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {accessLogs.map(entry => (
              <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--nx-surface-3)', border: '1px solid var(--nx-border)' }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-primary)', fontWeight: 500 }}>{entry.action}{entry.targetType ? ` · ${entry.targetType}` : ''}</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap' }}>{new Date(entry.createdAt).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Danger zone */}
      <Section id="danger" title="Delete Account" subtitle="Permanently delete your account and personal data" icon={<Trash2 size={15} />} open={open.danger} onToggle={() => toggle('danger')}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <AlertTriangle size={16} style={{ color: '#EF4444', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, color: 'var(--nx-text-primary)', margin: 0, marginBottom: 4, fontWeight: 500 }}>
              This permanently deletes your account
            </p>
            <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: 0, marginBottom: 12 }}>
              Your name, email, and login credentials will be removed. You'll need to confirm with a one-time
              code sent to your registered email. This cannot be undone.
            </p>
            <button
              onClick={openDeleteFlow}
              className="nx-btn nx-btn-sm"
              style={{ gap: 6, background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }}
            >
              <Trash2 size={13} /> Delete My Account
            </button>
          </div>
        </div>
      </Section>

      {/* Delete account — step 1: confirm */}
      <ConfirmDialog
        open={confirmingDelete && deleteStep === 'confirm'}
        variant="danger"
        title="Delete your account?"
        message="This permanently deletes your account and personal data. We'll email you a one-time code to confirm — this cannot be undone."
        confirmLabel="Send Confirmation Code"
        isLoading={deleteBusy}
        error={deleteError}
        onConfirm={requestOtp}
        onCancel={() => setConfirmingDelete(false)}
      />

      {/* Delete account — step 2: enter OTP */}
      {confirmingDelete && deleteStep === 'otp' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--nx-surface-2)', borderRadius: 12, padding: 28, maxWidth: 380, width: '90%', border: '1px solid var(--nx-border)' }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 8 }}>
              Enter confirmation code
            </h3>
            <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 16 }}>
              We sent a 6-digit code to your registered email. It expires in 10 minutes.
            </p>
            <input
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{ ...inputStyle, fontSize: 20, letterSpacing: '0.3em', textAlign: 'center', marginBottom: 12 }}
            />
            {deleteError && (
              <p style={{ fontSize: 12, color: '#EF4444', marginBottom: 12 }}>{deleteError}</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setConfirmingDelete(false)} disabled={deleteBusy} className="nx-btn nx-btn-sm">
                Cancel
              </button>
              <button
                onClick={confirmDeleteWithOtp}
                disabled={deleteBusy || otp.length !== 6}
                className="nx-btn nx-btn-sm"
                style={{ background: '#EF4444', color: '#fff', border: 'none', opacity: deleteBusy || otp.length !== 6 ? 0.6 : 1 }}
              >
                {deleteBusy ? 'Deleting…' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

// Email/phone double as login and OTP-login credentials, so changes only take effect once
// the NEW value is confirmed with a one-time code — mirrors the account-deletion OTP flow
// rather than saving instantly, to avoid accidental lockouts or transferring the credential
// to a number/address the user doesn't actually control.
const ChangeContactModal: React.FC<{
  open: boolean;
  kind: 'email' | 'phone';
  currentValue: string;
  onClose: () => void;
  onSaved: (updated: { email: string; phone: string | null }) => void;
}> = ({ open, kind, currentValue, onClose, onSaved }) => {
  const [step, setStep] = useState<'input' | 'otp'>('input');
  const [value, setValue] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStep('input');
      setValue('');
      setOtp('');
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const label = kind === 'email' ? 'email address' : 'mobile number';

  const sendCode = async () => {
    if (!value.trim()) return;
    setBusy(true);
    setError('');
    try {
      if (kind === 'email') await authApi.requestEmailChangeOtp(value.trim());
      else await authApi.requestPhoneChangeOtp(value.trim());
      setStep('otp');
    } catch (err) {
      setError((err as ApiError).message || 'Could not send the code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    if (otp.trim().length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const updated = kind === 'email'
        ? await authApi.confirmEmailChange(value.trim(), otp.trim())
        : await authApi.confirmPhoneChange(value.trim(), otp.trim());
      onSaved({ email: updated.email, phone: updated.phone });
      onClose();
    } catch (err) {
      setError((err as ApiError).message || 'Invalid or expired code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--nx-surface-2)', borderRadius: 12, padding: 28, maxWidth: 380, width: '90%', border: '1px solid var(--nx-border)' }}>
        {step === 'input' ? (
          <>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 8 }}>
              Change your {label}
            </h3>
            <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 16 }}>
              Current: {currentValue || 'Not set'}. We'll send a code to confirm the new {label} before it's saved.
            </p>
            <input
              value={value}
              onChange={e => setValue(kind === 'phone' ? e.target.value.replace(/[^0-9+ ]/g, '') : e.target.value)}
              placeholder={kind === 'email' ? 'you@example.com' : '+91 98765 43210'}
              style={{ ...inputStyle, marginBottom: 12 }}
              type={kind === 'email' ? 'email' : 'tel'}
            />
            {error && <p style={{ fontSize: 12, color: '#EF4444', marginBottom: 12 }}>{error}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={onClose} disabled={busy} className="nx-btn nx-btn-sm">Cancel</button>
              <button
                onClick={sendCode}
                disabled={busy || !value.trim()}
                className="nx-btn nx-btn-primary nx-btn-sm"
                style={{ opacity: busy || !value.trim() ? 0.6 : 1 }}
              >
                {busy ? 'Sending…' : 'Send Code'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 8 }}>
              Enter confirmation code
            </h3>
            <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 16 }}>
              We sent a 6-digit code to confirm your new {label}. It expires in 10 minutes.
            </p>
            <input
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{ ...inputStyle, fontSize: 20, letterSpacing: '0.3em', textAlign: 'center', marginBottom: 12 }}
            />
            {error && <p style={{ fontSize: 12, color: '#EF4444', marginBottom: 12 }}>{error}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={onClose} disabled={busy} className="nx-btn nx-btn-sm">Cancel</button>
              <button
                onClick={confirmCode}
                disabled={busy || otp.length !== 6}
                className="nx-btn nx-btn-primary nx-btn-sm"
                style={{ opacity: busy || otp.length !== 6 ? 0.6 : 1 }}
              >
                {busy ? 'Confirming…' : 'Confirm'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
